import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { TODAY, addDays, uc, fetchWithTimeout } from '../utils/dates';
import { progressThisUnit } from '../utils/routines';
import CyclingTitle from './primitives/CyclingTitle';
import EmptyState  from './primitives/EmptyState';
import styles from './CalendarPage.module.css';
import { useGoogleCalendarSync } from '../hooks/useGoogleCalendarSync';
import { formatRelativeDate, formatRelativeTime } from '../utils/formatDate';

function CalendarPage({ events=[], tasks=[], routines=[], toggleTask, view, setView, setSt, notify, autoSync, workKeywords = [] }) {
  const M_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const M_SHORT = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
  const DAY_S  = ["sun","mon","tue","wed","thu","fri","sat"];
  const DAY_FULL = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];

  const [viewDate, setViewDate]     = useState(new Date(TODAY));
  const [dayViewDate, setDayViewDate] = useState(TODAY); // ISO string for day view
  const [selEv,    setSelEv]        = useState(null);
  const [showAdd,  setShowAdd]      = useState(false);
  const [newForm,  setNewForm]      = useState({ title:"", date:TODAY, time:"", endTime:"", type:"work" });
  const [icsMsg,   setIcsMsg]       = useState("");
  const [gcalSyncing, setGcalSyncing] = useState(false);
  const [gcalMsg,  setGcalMsg]      = useState("");

  // ── Date helpers ──────────────────────────────────────────────────────────
  function to12h(t) {
    if (!t) return '';
    if (/^\d{2}:\d{2}$/.test(t)) {
      const [h, m] = t.split(':').map(Number);
      const ap = h >= 12 ? 'PM' : 'AM';
      return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${ap}`;
    }
    return t;
  }
  const fmtEventTime = (ev) => {
    if (!ev.time) return 'all day';
    const t = to12h(ev.time);
    if (ev.endTime) return `${t} — ${to12h(ev.endTime)}`;
    return t;
  };

  const fmtDayNav = (iso) => {
    if (iso === TODAY) return `today · ${DAY_FULL[new Date(iso+'T12:00:00').getDay()]}`;
    const d = new Date(iso+'T12:00:00');
    return `${DAY_FULL[d.getDay()]}, ${M_SHORT[d.getMonth()]} ${d.getDate()}`;
  };

  // ── Shift learning ────────────────────────────────────────────────────────
  const learnShiftDuration = (type) => {
    const pastEvents = (events||[]).filter(e => e.type === type && e.time && e.endTime && e.date < TODAY);
    if (pastEvents.length < 2) return null;
    const durations = pastEvents.map(e => {
      const parseTime = t => {
        if (!t) return null;
        const m = t.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (!m) return null;
        let h = parseInt(m[1]), min = parseInt(m[2]);
        if (m[3]?.toUpperCase() === "PM" && h !== 12) h += 12;
        if (m[3]?.toUpperCase() === "AM" && h === 12) h = 0;
        return h * 60 + min;
      };
      const start = parseTime(e.time);
      let end = parseTime(e.endTime);
      if (!start || !end) return null;
      if (end < start) end += 24 * 60;
      return end - start;
    }).filter(Boolean);
    if (durations.length === 0) return null;
    return Math.round(durations.reduce((a,b)=>a+b,0) / durations.length);
  };

  const suggestEndTime = (startTime, type) => {
    const durationMins = learnShiftDuration(type);
    if (!durationMins || !startTime) return "";
    const m = startTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);
    if (!m) return "";
    let h = parseInt(m[1]), min = parseInt(m[2]);
    if (m[3]?.toUpperCase() === "PM" && h !== 12) h += 12;
    if (m[3]?.toUpperCase() === "AM" && h === 12) h = 0;
    const totalMins = h * 60 + min + durationMins;
    const endH = Math.floor(totalMins / 60) % 24;
    const endMin = totalMins % 60;
    const period = endH >= 12 ? "PM" : "AM";
    const displayH = endH % 12 || 12;
    return `${displayH}:${String(endMin).padStart(2,"0")} ${period}`;
  };

  // ── Google Calendar sync via hook ────────────────────────────────────────
  const { syncFromGoogle, autoSyncIfStale, pushEventToGoogle, deleteGoogleEvent,
          syncing: gcalSyncing2, lastSynced, syncError: gcalSyncError } = useGoogleCalendarSync({ setSt, events });

  useEffect(() => {
    if (!autoSync) return;
    const t = setTimeout(() => autoSyncIfStale(), 1500);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  // ── Week/month calculations ───────────────────────────────────────────────
  const vM = viewDate.getMonth();
  const vY = viewDate.getFullYear();

  const weekStart = useMemo(() => {
    const d = new Date(viewDate);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0,0,0,0);
    return d;
  }, [viewDate]);

  const WEEK = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    const iso = d.toISOString().slice(0,10);
    return { lbl: DAY_S[d.getDay()], full: DAY_FULL[d.getDay()], num: d.getDate(), month: M_FULL[d.getMonth()], iso, isToday: iso === TODAY };
  }), [weekStart]);

  const mRows = useMemo(() => {
    const first = new Date(vY, vM, 1).getDay();
    const last  = new Date(vY, vM + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= last; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i+7));
    return rows;
  }, [vM, vY]);

  const toMin = s => {
    if (!s || s === "All day") return -1;
    const m = s.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (!m) return 0;
    let h = +m[1], mn = +m[2];
    if (m[3].toUpperCase() === "PM" && h !== 12) h += 12;
    if (m[3].toUpperCase() === "AM" && h === 12) h = 0;
    return h * 60 + mn;
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const prev = () => {
    if (view === "today") {
      const d = new Date(dayViewDate+'T12:00:00');
      d.setDate(d.getDate() - 1);
      setDayViewDate(d.toISOString().slice(0,10));
    } else {
      const d = new Date(viewDate);
      if (view === "week") d.setDate(d.getDate() - 7);
      else { d.setDate(1); d.setMonth(d.getMonth() - 1); }
      setViewDate(d);
    }
  };
  const next = () => {
    if (view === "today") {
      const d = new Date(dayViewDate+'T12:00:00');
      d.setDate(d.getDate() + 1);
      setDayViewDate(d.toISOString().slice(0,10));
    } else {
      const d = new Date(viewDate);
      if (view === "week") d.setDate(d.getDate() + 7);
      else { d.setDate(1); d.setMonth(d.getMonth() + 1); }
      setViewDate(d);
    }
  };
  const goToday = () => { setViewDate(new Date(TODAY)); setDayViewDate(TODAY); };

  const navigateToDayView = (iso) => {
    setDayViewDate(iso);
    setView('today');
  };

  // ── Event CRUD ────────────────────────────────────────────────────────────
  const saveEvent = () => {
    if (!newForm.title.trim()) return;
    const newEvent = { id:`e${Date.now()}`, ...newForm, source: newForm.source || 'calendar' };
    setSt(p => ({ ...p, events: [newEvent, ...p.events] }));
    notify('Event added');
    // Push to Google Calendar async (non-blocking)
    pushEventToGoogle(newEvent).catch(() => {});
    setNewForm({ title:'', date:TODAY, time:'', endTime:'', type:'work' });
    setShowAdd(false);
  };

  const deleteEvent = id => {
    const ev = events.find(e => e.id === id);
    setSt(p => ({ ...p, events: p.events.filter(e => e.id !== id) }));
    setSelEv(null);
    notify('Event removed');
    // If it has a Google ID, delete from Google Calendar too
    if (ev?.googleId) deleteGoogleEvent(ev.googleId).catch(() => {});
  };

  // ── ICS import ────────────────────────────────────────────────────────────
  const importICS = txt => {
    setIcsMsg("Parsing…");
    try {
      const lines = txt.replace(/\r\n/g,"\n").replace(/\r/g,"\n").split("\n");
      const unfolded = [];
      for (const l of lines) {
        if ((l.startsWith(" ")||l.startsWith("\t")) && unfolded.length > 0) unfolded[unfolded.length-1] += l.slice(1);
        else unfolded.push(l);
      }
      const evs = []; let inEv = false, cur = {};
      for (const line of unfolded) {
        if (line === "BEGIN:VEVENT") { inEv = true; cur = {}; continue; }
        if (line === "END:VEVENT" && inEv) { inEv = false; if (cur.title && cur.date) evs.push(cur); continue; }
        if (!inEv) continue;
        const sep = line.indexOf(":"); if (sep === -1) continue;
        const key = line.slice(0,sep).split(";")[0].toUpperCase();
        const val = line.slice(sep+1).trim();
        if (key === "SUMMARY") cur.title = val.replace(/\\,/g,",").trim();
        if (key === "DTSTART") {
          const raw = val.replace(/Z$/,"");
          cur.date = `${raw.slice(0,4)}-${raw.slice(4,6)}-${raw.slice(6,8)}`;
          if (raw.length >= 15) {
            let h = parseInt(raw.slice(9,11)); const mn = raw.slice(11,13);
            const ap = h >= 12 ? "PM" : "AM"; if (h > 12) h -= 12; if (h === 0) h = 12;
            cur.time = `${h}:${mn} ${ap}`;
          }
        }
        if (!cur.type) cur.type = "work";
      }
      if (evs.length === 0) { setIcsMsg("No events found."); return; }
      setSt(p => {
        const ex = new Set(p.events.map(e => `${e.date}|${e.title.toLowerCase()}`));
        const toAdd = evs.filter(e => !ex.has(`${e.date}|${(e.title||"").toLowerCase()}`))
          .map(e => ({ id:`ics${Date.now()}${Math.random()}`, ...e, source:"ics" }));
        return { ...p, events: [...p.events, ...toAdd] };
      });
      setIcsMsg(`✓ ${evs.length} events imported`);
      notify(`Imported ${evs.length} events`);
    } catch(e) { setIcsMsg("Error: " + e.message); }
  };

  // ── GCal sync (delegates to useGoogleCalendarSync hook) ─────────────────
  const [gcalConnected, setGcalConnected] = useState(true);

  const syncGCal = async (silent = false) => {
    setGcalMsg('');
    try {
      const count = await syncFromGoogle(silent);
      setGcalConnected(true);
      if (count !== undefined) {
        const total = typeof count === 'object' ? count.total : count;
        const added = typeof count === 'object' ? count.added : count;
        setGcalMsg(`✓ ${total} Google events · ${added} new`);
      }
    } catch (e) {
      if (e.code === 'NOT_CONNECTED' || e.message === 'NOT_CONNECTED') {
        setGcalConnected(false);
        if (!silent) setGcalMsg('connect Google Calendar to sync');
      } else {
        if (!silent) setGcalMsg('⚠ ' + (e.message || 'sync failed'));
      }
    }
  };

  const evColor = t => t === "health" ? "var(--accent-s)" : t === "personal" ? "var(--accent-d)" : "var(--accent-g)";
  const evBg    = t => t === "health" ? "var(--accent-w)" : t === "personal" ? "rgba(138,63,46,.12)" : "rgba(166,126,74,.12)";

  // ── Shared UI fragments ───────────────────────────────────────────────────
  const HeaderBar = () => (
    <div className={styles.header}>
      <div className={styles.headerTop}>
        <CyclingTitle pageLabel="calendar" filters={["today","month","week"]} value={view} onChange={setView} alwaysShowSuffix={true} />
        <div className={styles.headerActions}>
          <button onClick={() => { if(!showAdd) setNewForm({title:"",date:view==="today"?dayViewDate:TODAY,time:"",endTime:"",type:"work"}); setShowAdd(p=>!p); }} className={styles.addEventBtn}>+ event</button>
        </div>
      </div>
      <div style={{display:'flex',alignItems:'center',paddingTop:4,cursor:'pointer',minHeight:18}} onClick={() => syncGCal(false)}>
        {gcalSyncing2 ? (
          <span style={{display:'flex',alignItems:'center',gap:4}}>
            <span style={{width:3,height:3,borderRadius:'50%',background:'var(--t4)',display:'inline-block',animation:'tr-pulse 1s ease-in-out infinite'}}/>
            <span style={{width:3,height:3,borderRadius:'50%',background:'var(--t4)',display:'inline-block',animation:'tr-pulse 1s ease-in-out infinite',animationDelay:'.15s'}}/>
            <span style={{width:3,height:3,borderRadius:'50%',background:'var(--t4)',display:'inline-block',animation:'tr-pulse 1s ease-in-out infinite',animationDelay:'.3s'}}/>
            <span style={{fontFamily:'var(--sans)',fontSize:10,color:'var(--t4)',marginLeft:2}}>syncing</span>
          </span>
        ) : (
          <span style={{fontFamily:'var(--sans)',fontSize:10,color:'var(--t4)'}}>
            {lastSynced ? `sync · ${formatRelativeTime(lastSynced)}` : 'sync'}
          </span>
        )}
      </div>
    </div>
  );

  const GcalBanner = () => gcalMsg ? (
    <div className={`${styles.syncBanner} ${gcalMsg.startsWith("✓") ? styles.syncBannerSuccess : styles.syncBannerError}`}>
      <span className={styles.syncBannerMsg}>{gcalMsg}</span>
      {gcalMsg.startsWith("⚠") && <button style={{fontFamily:"var(--sans)",fontSize:11,color:"var(--t3)",background:"transparent",border:"none",cursor:"pointer",marginLeft:8}} onClick={()=>syncGCal(false)}>retry</button>}
      {/auth|api[-_ ]?key|unauthorized/i.test(gcalMsg) && (
        <button onClick={() => syncGCal(false)} className={styles.reconnectBtn}>reconnect</button>
      )}
      <button onClick={() => setGcalMsg("")} aria-label="Dismiss" className={styles.dismissBtn}>×</button>
    </div>
  ) : null;

  const AddForm = () => !showAdd ? null : (
    <div className={styles.addForm}>
      <div className="section-label mb3">New Event</div>
      <div className="col-g3">
        <div className="field mb0">
          <label>Title</label>
          <input value={newForm.title} onChange={e=>setNewForm(p=>({...p,title:e.target.value}))}
            placeholder="Event title…" autoFocus autoComplete="off" autoCorrect="off" autoCapitalize="off"
            onKeyDown={e=>{ if(e.key==="Enter") saveEvent(); if(e.key==="Escape") setShowAdd(false); }} />
        </div>
        <div className={styles.addFormGrid}>
          <div className="field mb0"><label>Date</label><input type="date" value={newForm.date} onChange={e=>setNewForm(p=>({...p,date:e.target.value}))} /></div>
          <div className="field mb0">
            <label>Start Time</label>
            <input value={newForm.time} onChange={e => { const t=e.target.value; const s=suggestEndTime(t,newForm.type); setNewForm(p=>({...p,time:t,endTime:p.endTime||s||p.endTime})); }} placeholder="9:00 AM" autoComplete="off" />
          </div>
          <div className="field mb0">
            <label className="row-g2-center">
              End Time
              {newForm.time && suggestEndTime(newForm.time,newForm.type) && !newForm.endTime && (
                <span className={styles.suggestLink} onClick={()=>setNewForm(p=>({...p,endTime:suggestEndTime(p.time,p.type)}))}>· suggest</span>
              )}
            </label>
            <input value={newForm.endTime} onChange={e=>setNewForm(p=>({...p,endTime:e.target.value}))} placeholder="5:00 PM" autoComplete="off"
              style={{borderColor:newForm.endTime?undefined:newForm.time&&suggestEndTime(newForm.time,newForm.type)?"var(--accent-s)":undefined}} />
          </div>
        </div>
        <div>
          <div className={styles.typeLabel}>Type</div>
          <div className={styles.typeRow}>
            {["work","health","personal"].map(t => (
              <button key={t} onClick={()=>setNewForm(p=>({...p,type:t}))} style={{ flex:1, height:"var(--btn-h)", background:newForm.type===t?evBg(t):"transparent", border:`1px solid ${newForm.type===t?evColor(t):"var(--b2)"}`, borderRadius:"var(--r2)", color:newForm.type===t?evColor(t):"var(--t2)", fontSize:"var(--fs-base)", cursor:"pointer", fontWeight:newForm.type===t?600:400, textTransform:"lowercase", WebkitTapHighlightColor:"transparent" }}>{t}</button>
            ))}
          </div>
        </div>
        {(() => { const d=learnShiftDuration(newForm.type); return d ? <div className={styles.learnedHint}>✦ Based on past {newForm.type} events, typical duration is {Math.floor(d/60)}h{d%60>0?` ${d%60}m`:""}</div> : null; })()}
        <div className={styles.addFormActions}>
          <button onClick={saveEvent} className={styles.saveBtn}>save event</button>
          <button onClick={()=>setShowAdd(false)} className={styles.cancelIconBtn}>×</button>
        </div>
      </div>
    </div>
  );



  // ── Event row component ───────────────────────────────────────────────────
  const EventRowItem = ({ ev }) => (
    <div className={styles.eventRow} onClick={() => setSelEv(ev)}>
      <div className={styles.eventAccentLine} />
      <div className={styles.eventBody}>
        <div className={styles.eventTitle}>{ev.source==='train'?(ev.title||'').toLowerCase():ev.title}</div>
        <div className={styles.eventTime}>{fmtEventTime(ev)}</div>
      </div>
    </div>
  );

  // ── Habit check-in (shared across today/day views) ────────────────────────
  const checkInRoutine = id => {
    setSt(p => {
      const ex = (p.routines||[]).find(r=>r.id===id);
      if (!ex) return p;
      const target = ex.target||{count:1,unit:'day'};
      const tISO = TODAY, nowISO = new Date().toISOString().slice(0,16);
      if (target.count===1) {
        const done = (ex.history||[]).some(h=>h.slice(0,10)===tISO);
        if (done) { notify(`${ex.name} unchecked`); return {...p,routines:(p.routines||[]).map(r=>r.id!==id?r:{...r,lastDone:'',history:(r.history||[]).filter(h=>h.slice(0,10)!==tISO)})}; }
        notify(`${ex.name} done`);
        return {...p,routines:(p.routines||[]).map(r=>r.id!==id?r:{...r,lastDone:tISO,history:[...(r.history||[]),tISO]})};
      }
      const cnt = (ex.history||[]).filter(h=>h.slice(0,10)===tISO).length;
      if (cnt >= target.count) return p;
      notify(`${ex.name}: ${cnt+1}/${target.count}`);
      return {...p,routines:(p.routines||[]).map(r=>r.id!==id?r:{...r,lastDone:tISO,history:[...(r.history||[]),nowISO]})};
    });
  };


  // ── Streak helper ─────────────────────────────────────────────────────────
  const getStreak = (routine) => {
    const hist = (routine.history || []).map(h => h.slice(0,10)).sort();
    if (hist.length === 0) return 0;
    let streak = 0;
    let check = new Date(TODAY + 'T12:00:00');
    // Allow today or yesterday as most recent
    const last = hist[hist.length - 1];
    const diffFromToday = Math.round((new Date(TODAY+'T12:00:00') - new Date(last+'T12:00:00')) / 86400000);
    if (diffFromToday > 1) return 0; // broken streak
    const histSet = new Set(hist);
    while (true) {
      const iso = check.toISOString().slice(0,10);
      if (!histSet.has(iso)) break;
      streak++;
      check.setDate(check.getDate() - 1);
    }
    return streak;
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // TODAY / DAY VIEW
  // ═══════════════════════════════════════════════════════════════════════════
  const renderDayView = () => {
    const iso = dayViewDate;
    const dayEvs = (events||[]).filter(e=>e.date===iso).sort((a,b)=>toMin(a.time)-toMin(b.time));
    const dueTasks = (tasks||[]).filter(t=>t.dueDate===iso||(t.bucket==='today'&&iso===TODAY));
    const pendingTasks = dueTasks.filter(t=>!t.done);
    const doneTasks = dueTasks.filter(t=>t.done);
    const allHabits = routines||[];
    const habitsChecked = allHabits.filter(r=>{ const tgt=r.target||{count:1,unit:'day'}; return progressThisUnit(r)>=tgt.count; });

    // Build slim 3-part day nav
    const offsetISO = (base, days) => {
      const d = new Date(base+'T12:00:00'); d.setDate(d.getDate()+days);
      return d.toISOString().slice(0,10);
    };
    const fmtNavDate = (iso2) => {
      const d = new Date(iso2+'T12:00:00');
      return `${M_SHORT[d.getMonth()]} ${d.getDate()}`;
    };
    const fmtCenterDate = (iso2) => {
      const d = new Date(iso2+'T12:00:00');
      return `${M_SHORT[d.getMonth()]} ${d.getDate()} · ${DAY_FULL[d.getDay()]}`;
    };
    const prevISO = offsetISO(iso,-1);
    const nextISO = offsetISO(iso,1);

    return (
      <>
        {/* Slim 3-part day nav */}
        <div className={styles.dayNavBar}>
          <button className={styles.dayNavSide} onClick={prev}>‹ {fmtNavDate(prevISO)}</button>
          <span className={styles.dayNavCenter}>{fmtCenterDate(iso)}</span>
          <button className={styles.dayNavSide} onClick={next}>{fmtNavDate(nextISO)} ›</button>
        </div>

        <div className={styles.viewBody}>
          {/* Events section */}
          <div className={styles.section}>
            <div className={styles.sectionHdr}>
              <span className={styles.sectionLbl}>events</span>
              {dayEvs.length===0
                ? <button className={styles.inlineAdd} onClick={()=>{ setNewForm(p=>({...p,date:iso})); setShowAdd(true); }}>+ add</button>
                : <span className={styles.counterPill}>{dayEvs.length}</span>
              }
            </div>
            {dayEvs.length===0
              ? <div className={styles.emptyLine}>nothing scheduled</div>
              : <div className={styles.eventList}>{dayEvs.map(ev=><EventRowItem key={ev.id} ev={ev}/>)}</div>
            }
          </div>

          <div className={styles.sectionDivider}/>

          {/* Due today section */}
          <div className={styles.section}>
            <div className={styles.sectionHdr}>
              <span className={styles.sectionLbl}>due today</span>
              {dueTasks.length>0&&<span className={styles.counterPill}>{doneTasks.length} / {dueTasks.length} done</span>}
            </div>
            {dueTasks.length===0
              ? <div className={styles.emptyLine}>nothing due</div>
              : <div className={styles.taskList}>
                  {[...pendingTasks,...doneTasks].map(t=>(
                    <div key={t.id} className={styles.taskRow} onClick={()=>toggleTask&&toggleTask(t.id)}>
                      <div className={`${styles.taskCheck} ${t.done?styles.taskCheckDone:''}`}>
                        {t.done&&<svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                      <span className={`${styles.taskText} ${t.done?styles.taskTextDone:''}`}>{t.text}</span>
                    </div>
                  ))}
                </div>
            }
          </div>

          <div className={styles.sectionDivider}/>

          {/* Habits section (today only) */}
          {iso===TODAY&&(
            <div className={styles.section}>
              <div className={styles.sectionHdr}>
                <span className={styles.sectionLbl}>habits</span>
                {allHabits.length>0&&<span className={styles.counterPill}>{habitsChecked.length} / {allHabits.length} today</span>}
              </div>
              {allHabits.length===0
                ? <div className={styles.emptyLine}>no habits set</div>
                : <div className={styles.habitList}>
                    {allHabits.map(r=>{
                      const tgt=r.target||{count:1,unit:'day'}, prog=progressThisUnit(r), done=prog>=tgt.count;
                      const streak = getStreak(r);
                      return (
                        <div key={r.id} className={styles.habitRow}>
                          <div className={`${styles.habitCheck} ${done?styles.habitCheckDone:''}`} onClick={()=>checkInRoutine(r.id)}>
                            {done&&<svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                          <span className={styles.habitName} style={{color:done?'var(--t3)':'var(--ink)',textDecoration:done?'line-through':'none'}}>{r.name}</span>
                          {streak>=2&&<span className={styles.streakPill}>{streak} day streak</span>}
                        </div>
                      );
                    })}
                  </div>
              }
            </div>
          )}
        </div>
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // WEEK VIEW
  // ═══════════════════════════════════════════════════════════════════════════
  const renderWeekView = () => {
    const todayEvs = (events||[]).filter(e=>e.date===TODAY).sort((a,b)=>toMin(a.time)-toMin(b.time));
    const eventsByDay = {};
    (events||[]).forEach(ev=>{ if(!eventsByDay[ev.date]) eventsByDay[ev.date]=[]; eventsByDay[ev.date].push(ev); });
    const tasksByDay = {};
    (tasks||[]).forEach(t=>{ const k=t.dueDate; if(k&&WEEK.some(w=>w.iso===k)){ if(!tasksByDay[k])tasksByDay[k]=[]; tasksByDay[k].push(t); } });
    const totalWeekEvents = WEEK.reduce((s,w)=>(eventsByDay[w.iso]||[]).length+s, 0);
    const totalWeekTasks = WEEK.reduce((s,w)=>(tasksByDay[w.iso]||[]).length+s, 0);
    const habitsTotal = (routines||[]).length;
    const habitsToday = (routines||[]).filter(r=>{ const tgt=r.target||{count:1,unit:'day'}; return progressThisUnit(r)>=tgt.count; }).length;

    const weekEndISO = WEEK[6].iso;
    const ws = new Date(WEEK[0].iso+'T12:00:00'), we = new Date(weekEndISO+'T12:00:00');
    const weekLabel = ws.getMonth()===we.getMonth()
      ? `${M_SHORT[ws.getMonth()]} ${ws.getDate()} — ${we.getDate()} · ${ws.getFullYear()}`
      : `${M_SHORT[ws.getMonth()]} ${ws.getDate()} — ${M_SHORT[we.getMonth()]} ${we.getDate()} · ${ws.getFullYear()}`;

    const todayD = new Date(TODAY+'T12:00:00');
    const todayLabel = `${M_SHORT[todayD.getMonth()]} ${todayD.getDate()}`;

    return (
      <div className={styles.weekViewWrap}>
        {/* Week range nav — 3-zone, matches day view pattern */}
        <div className={styles.navRow}>
          <button onClick={prev} className={styles.navBtn}>‹ {(()=>{const p=new Date(ws);p.setDate(p.getDate()-7);return `${M_SHORT[p.getMonth()]} ${p.getDate()}`;})()}</button>
          <span className={styles.navLabel}>{weekLabel}</span>
          <button onClick={next} className={styles.navBtn}>{(()=>{const n=new Date(we);n.setDate(n.getDate()+7);return `${M_SHORT[n.getMonth()]} ${n.getDate()}`;})()}  ›</button>
        </div>

        {/* Day strip */}
        <div className={styles.dayStripWrap}>
          <div className={styles.dayStrip}>
            {WEEK.map(({iso,lbl,num,isToday})=>{
              const evCount = (eventsByDay[iso]||[]).length + (tasksByDay[iso]||[]).length;
              return (
                <button key={iso}
                  className={`${styles.dayStripCell} ${isToday?styles.dayStripCellToday:''}`}
                  onClick={()=>navigateToDayView(iso)}>
                  <span className={styles.dayStripDay}>{lbl}</span>
                  <span className={styles.dayStripDate}>{num}</span>
                  {evCount>0&&<span className={styles.dayStripDot}/>}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.viewBody}>
          {/* Today section */}
          <div className={styles.section}>
            <div className={styles.sectionHdr}>
              <span className={styles.sectionLbl}>today</span>
              <span className={styles.sectionAccentDate}>{todayLabel}</span>
            </div>
            {todayEvs.length===0
              ? <div className={styles.emptyLine}>no events today</div>
              : <div className={styles.eventList}>{todayEvs.map(ev=><EventRowItem key={ev.id} ev={ev}/>)}</div>
            }
          </div>

          <div className={styles.sectionDivider}/>

          {/* This week stats */}
          <div className={styles.section}>
            <div className={styles.sectionHdr}>
              <span className={styles.sectionLbl}>this week</span>
            </div>
            <div className={styles.weekStatsGrid}>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>events</span>
                <span className={styles.statValue}>{totalWeekEvents}</span>
                <span className={styles.statSub}>{totalWeekEvents===1?'this week':'this week'}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>tasks due</span>
                <span className={styles.statValue}>{totalWeekTasks}</span>
                <span className={styles.statSub}>{totalWeekTasks===1?'pending':'pending'}</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statLabel}>habits</span>
                <span className={styles.statValue}>{habitsToday}/{habitsTotal}</span>
                <span className={styles.statSub}>done today</span>
              </div>
            </div>
          </div>

          <div className={styles.sectionDivider}/>

          {/* Upcoming this week */}
          <div className={styles.section}>
            <div className={styles.sectionHdr}>
              <span className={styles.sectionLbl}>upcoming</span>
            </div>
            {WEEK.filter(w=>w.iso>=TODAY).map(({iso,lbl,num})=>{
              const dayEvs2=(eventsByDay[iso]||[]).sort((a,b)=>toMin(a.time)-toMin(b.time));
              const dayTasks2=tasksByDay[iso]||[];
              if(dayEvs2.length===0&&dayTasks2.length===0) return null;
              return (
                <div key={iso} style={{marginBottom:'var(--sp3)'}}>
                  <div style={{fontFamily:'var(--sans)',fontSize:10,color:iso===TODAY?'var(--accent)':'var(--t3)',letterSpacing:'0.04em',marginBottom:4,textTransform:'lowercase'}}>{lbl} {num}{iso===TODAY?' · today':''}</div>
                  {dayEvs2.map(ev=><div key={ev.id} onClick={()=>setSelEv(ev)} style={{display:'flex',alignItems:'center',gap:'var(--sp2)',padding:'3px 0',cursor:'pointer'}}>
                    <div style={{width:2,height:12,background:'var(--accent)',borderRadius:1,flexShrink:0}}/>
                    <span style={{fontFamily:'var(--sans)',fontSize:'var(--fs-xs)',color:'var(--t2)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ev.title}</span>
                    <span style={{fontFamily:'var(--sans)',fontSize:10,color:'var(--t4)'}}>{ev.time||''}</span>
                  </div>)}
                  {dayTasks2.map(t=><div key={t.id} style={{display:'flex',alignItems:'center',gap:'var(--sp2)',padding:'3px 0'}}>
                    <div style={{width:2,height:12,background:'var(--accent-s)',borderRadius:1,flexShrink:0}}/>
                    <span style={{fontFamily:'var(--sans)',fontSize:'var(--fs-xs)',color:'var(--t3)',flex:1}}>due: {t.text}</span>
                  </div>)}
                </div>
              );
            }).filter(Boolean)}
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════════════════
  // MONTH VIEW
  // ═══════════════════════════════════════════════════════════════════════════
  const renderMonthView = () => {
    // Build per-day data maps for the visible month
    const allEvents = events || [];
    const allTasks  = tasks  || [];
    const allRoutines = routines || [];

    // For habit dots: a day has a habit if any routine has a history entry on that date
    // OR if it's today and the habit is due (count > progressThisUnit)
    const habitDaySet = new Set();
    allRoutines.forEach(r => {
      (r.history || []).forEach(h => habitDaySet.add(h.slice(0,10)));
      // Mark today if any habit is due today
      if (progressThisUnit(r) < (r.target?.count ?? 1)) habitDaySet.add(TODAY);
    });

    const monthPrev = () => { const d=new Date(viewDate); d.setDate(1); d.setMonth(d.getMonth()-1); setViewDate(d); };
    const monthNext = () => { const d=new Date(viewDate); d.setDate(1); d.setMonth(d.getMonth()+1); setViewDate(d); };

    const monthLabel = `${M_FULL[vM].toLowerCase()} ${vY}`;

    return (
      <div className={styles.monthWrap}>
        {/* Month nav header — 3-zone, matches day view pattern */}
        <div className={styles.monthNavRow}>
          <button className={styles.monthNavBtn} onClick={monthPrev}>‹ {M_FULL[vM===0?11:vM-1].toLowerCase()}</button>
          <span className={styles.monthNavLabel}>{monthLabel}</span>
          <button className={styles.monthNavBtn} onClick={monthNext}>{M_FULL[vM===11?0:vM+1].toLowerCase()} ›</button>
        </div>

        {/* Day-of-week headers */}
        <div className={styles.monthDowRow}>
          {["s","m","t","w","t","f","s"].map((d,i)=>(
            <div key={i} className={styles.monthDow}>{d}</div>
          ))}
        </div>

        {/* Grid */}
        <div className={styles.monthGrid}>
          {mRows.map((row,ri)=>(
            <div key={ri} className={styles.monthGridRow}>
              {row.map((d,ci)=>{
                if (!d) return <div key={ci} className={styles.monthCellEmpty} />;
                const iso = `${vY}-${String(vM+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                const isToday = iso === TODAY;
                const isWeekend = ci===0 || ci===6;
                const dayEvs = allEvents.filter(e=>e.date===iso);
                // Dot types: work, event, task, habit
                const hasWork  = dayEvs.some(e=>e.type==="work");
                const hasEvent     = dayEvs.some(e=>e.type!=="work" && e.source!=='train');
                const hasTrainRun  = dayEvs.some(e=>e.dot==='training-run'||e.source==='train'&&['Long Run','Easy Run','Tempo Run','Interval'].includes(e.workoutType||e.title?.split(' · ')[0]));
                const hasTrainStr  = dayEvs.some(e=>e.dot==='training-strength'||e.source==='train'&&['Strength','Gym'].includes(e.workoutType||e.title?.split(' · ')[0]));
                const hasTask      = allTasks.some(t=>t.dueDate===iso);
                const hasHabit     = habitDaySet.has(iso);
                // Max 3 dots — priority: work > event > training > task > habit
                const dotList = [];
                if (hasWork)                         dotList.push("work");
                if (hasEvent && dotList.length < 3)  dotList.push("event");
                if (hasTrainRun && dotList.length < 3)  dotList.push("training-run");
                if (hasTrainStr && !hasTrainRun && dotList.length < 3) dotList.push("training-strength");
                if (hasTask && dotList.length < 3)   dotList.push("task");
                if (hasHabit && dotList.length < 3)  dotList.push("habit");
                const dots = dotList.slice(0,3);

                return (
                  <div key={ci}
                    className={`${styles.monthCell} ${isWeekend?styles.monthCellWeekend:""}`}
                    onClick={()=>navigateToDayView(iso)}>
                    {/* Date number */}
                    {isToday
                      ? <div className={styles.monthTodayCircle}>{d}</div>
                      : <div className={`${styles.monthDate} ${isWeekend?styles.monthDateWeekend:""}`}>{d}</div>
                    }
                    {/* Dot cluster */}
                    {dots.length > 0 && (
                      <div className={styles.monthDots}>
                        {dots.map((type,i)=>(
                          <div key={i} className={`${styles.monthDot} ${styles["monthDot_"+type]}`} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className={styles.monthLegend}>
          {[
            {type:"work",          label:"work / shift"},
            {type:"event",         label:"event"},
            {type:"training-run",  label:"training"},
            {type:"task",          label:"task due"},
            {type:"habit",         label:"habit"},
          ].map(({type,label})=>(
            <div key={type} className={styles.monthLegendItem}>
              <div className={`${styles.monthLegendDot} ${styles["monthDot_"+type]}`} />
              <span className={styles.monthLegendLabel}>{label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className={styles.root}>
      <HeaderBar />
      <GcalBanner />
      <AddForm />
      {view==="today"  && renderDayView()}
      {view==="week"   && renderWeekView()}
      {view==="month"  && renderMonthView()}

      {/* ── EVENT DETAIL MODAL ── */}
      {selEv&&(
        <div onClick={()=>setSelEv(null)} className={styles.modalOverlay}>
          <div onClick={e=>e.stopPropagation()} className={styles.modalSheet}>
            <div className={styles.modalHandle} />
            <div className={styles.modalTypeLine}>
              <span className={styles.modalType} style={{color:evColor(selEv.type)}}>{selEv.type}{selEv.source?` · ${selEv.source}`:""}</span>
            </div>
            <div className={styles.modalTitle}>{selEv.title}</div>
            <div className={styles.modalTime}>{selEv.time||"all day"}</div>
            <div className={styles.modalDate}>{new Date(selEv.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}</div>
            <div className="g2 mb4">
              <div className="field" style={{margin:0,gridColumn:"1/-1"}}><label>Title</label><input type="text" autoComplete="off" autoCorrect="off" value={selEv.title} onChange={e=>setSelEv(p=>({...p,title:e.target.value}))} /></div>
              <div className="field mb0"><label>Date</label><input type="date" value={selEv.date} onChange={e=>setSelEv(p=>({...p,date:e.target.value}))} /></div>
              <div className="field mb0"><label>Type</label><select value={selEv.type||"work"} onChange={e=>setSelEv(p=>({...p,type:e.target.value}))}><option value="work">Work</option><option value="health">Health</option><option value="personal">Personal</option></select></div>
              <div className="field mb0"><label>Start Time</label><input type="text" autoComplete="off" autoCorrect="off" value={selEv.time||""} onChange={e=>setSelEv(p=>({...p,time:e.target.value}))} placeholder="e.g. 9:00 AM" /></div>
              <div className="field mb0"><label>End Time</label><input type="text" autoComplete="off" autoCorrect="off" value={selEv.endTime||""} onChange={e=>setSelEv(p=>({...p,endTime:e.target.value}))} placeholder="e.g. 5:00 PM" /></div>
            </div>
            <div className={styles.modalActions}>
              <button onClick={()=>deleteEvent(selEv.id)} className={styles.modalDeleteBtn}>×</button>
              <button onClick={()=>setSelEv(null)} className={styles.modalCancelBtn}>cancel</button>
              <button onClick={()=>{ setSt(p=>({...p,events:p.events.map(e=>e.id===selEv.id?{...selEv}:e)})); setSelEv(null); notify("Event updated"); }} className={styles.modalSaveBtn}>save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarPage;
